import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { PageContainer } from "@/components/layout/PageContainer";
import { PrivateLayout } from "@/components/layout/PrivateLayout";
import { ScrollToTop } from "@/components/layout/ScrollToTop";
import { HOME_HERO_HEIGHT, PAGE_HERO_HEIGHT } from "@/lib/heroLayout";

import Home from "@/routes/home";
import About from "@/routes/about";
import ReleasesIndex from "@/routes/releases/index";
import ReleasePage from "@/routes/releases/$slug";
import LiveIndex from "@/routes/live/index";
import LivePage from "@/routes/live/$slug";
import Press from "@/routes/press";
import Contact from "@/routes/contact";
import Access from "@/routes/access";
import PrivateIndex from "@/routes/private/index";
import PrivateUnreleased from "@/routes/private/unreleased";
import PrivateVault from "@/routes/private/vault";
import PrivateEdits from "@/routes/private/edits";
import PrivateAssets from "@/routes/private/assets";
import NotFound from "@/routes/not-found";

function Root() {
  return (
    <AppShell>
      <ScrollToTop />
      <Outlet />
    </AppShell>
  );
}

// Layout route: wraps children in PageContainer. Used for everything except
// the home route (which renders its own full-bleed hero) and /private/* (which
// uses PrivateLayout, itself already containing a PageContainer).
function ContainedLayout() {
  return (
    <PageContainer>
      <Outlet />
    </PageContainer>
  );
}

export const router = createBrowserRouter([
  {
    path: "/",
    element: <Root />,
    children: [
      // Routes with their own hero banner manage their own top padding —
      // no ContainedLayout wrapping. `handle.heroHeight` sizes the persistent
      // hero video layer (PersistentHeroBackdrop); routes without it (detail
      // pages, forms) render no video band.
      { index: true, element: <Home />, handle: { heroHeight: HOME_HERO_HEIGHT } },
      { path: "about", element: <About />, handle: { heroHeight: PAGE_HERO_HEIGHT } },
      { path: "releases", element: <ReleasesIndex />, handle: { heroHeight: PAGE_HERO_HEIGHT } },
      // Detail page uses ReleaseBackdrop (cover art), not the video hero.
      { path: "releases/:slug", element: <ReleasePage /> },
      { path: "live", element: <LiveIndex />, handle: { heroHeight: PAGE_HERO_HEIGHT } },
      { path: "live/:slug", element: <LivePage />, handle: { heroHeight: PAGE_HERO_HEIGHT } },
      { path: "press", element: <Press />, handle: { heroHeight: PAGE_HERO_HEIGHT } },
      { path: "contact", element: <Contact />, handle: { heroHeight: PAGE_HERO_HEIGHT } },
      // Form / utility routes get the default contained layout with nav offset.
      {
        element: <ContainedLayout />,
        children: [
          { path: "access", element: <Access /> },
          { path: "*", element: <NotFound /> },
        ],
      },
      {
        path: "private",
        element: <PrivateLayout />,
        children: [
          { index: true, element: <PrivateIndex /> },
          { path: "unreleased", element: <PrivateUnreleased /> },
          { path: "vault", element: <PrivateVault /> },
          { path: "edits", element: <PrivateEdits /> },
          { path: "assets", element: <PrivateAssets /> },
        ],
      },
    ],
  },
  // Convenience: bare /private/* without an existing session sends people to /access first
  { path: "/login", element: <Navigate to="/access" replace /> },
]);
